import React, { useState, useRef, useEffect } from 'react';

/**
 * Custom listbox replacing native <select>/<optgroup> where the caller needs
 * to control popup direction - native select popups pick up/down direction
 * based on browser/OS heuristics that can't be overridden with CSS, which
 * caused the Cost Explorer's "Group By Dimension" list (grouped, several
 * items tall) to occasionally open upward and run off the top of the
 * viewport. This always opens below the trigger with a capped, scrollable
 * height instead.
 */

export interface DropdownOption {
  value: string;
  label: string;
}

export interface DropdownGroup {
  /** Omit for the top-level, ungrouped set of options. */
  label?: string;
  options: DropdownOption[];
}

interface GroupedDropdownProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  groups: DropdownGroup[];
  ariaLabel?: string;
  className?: string;
}

export const GroupedDropdown: React.FC<GroupedDropdownProps> = ({
  id,
  value,
  onChange,
  groups,
  ariaLabel,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const allOptions = groups.flatMap((g) => g.options);
  const selected = allOptions.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOptionClick = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    buttonRef.current?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      buttonRef.current?.focus();
      return;
    }
    if ((event.key === 'Enter' || event.key === ' ') && !isOpen) {
      event.preventDefault();
      setIsOpen(true);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        return;
      }
      const currentIndex = allOptions.findIndex((opt) => opt.value === value);
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      const nextIndex = (currentIndex + delta + allOptions.length) % allOptions.length;
      onChange(allOptions[nextIndex].value);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        ref={buttonRef}
        id={id}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        className="flex w-full items-center justify-between rounded-md border border-border bg-card px-3 py-2 text-left text-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring dark:border-border dark:bg-secondary dark:text-white"
      >
        <span className="truncate">{selected?.label ?? 'Select...'}</span>
        <svg
          className={`h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform dark:text-muted-foreground/70 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <ul
          role="listbox"
          aria-label={ariaLabel}
          className="absolute left-0 top-full z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-border bg-card py-1 shadow-lg dark:border-border dark:bg-card"
        >
          {groups.map((group, groupIndex) => (
            <React.Fragment key={group.label ?? `group-${groupIndex}`}>
              {group.label && (
                <li
                  role="presentation"
                  className="mt-1 border-t border-border px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground/70 first:mt-0 first:border-t-0 dark:border-border dark:text-muted-foreground"
                >
                  {group.label}
                </li>
              )}
              {group.options.map((option) => {
                const isSelected = option.value === value;
                return (
                  <li
                    key={option.value}
                    role="option"
                    aria-selected={isSelected}
                    tabIndex={0}
                    onClick={() => handleOptionClick(option.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleOptionClick(option.value);
                      }
                    }}
                    className={`cursor-pointer px-3 py-2 text-sm transition-colors ${
                      isSelected
                        ? 'bg-primary/10 text-primary dark:bg-primary/10 dark:text-primary'
                        : 'text-foreground/80 hover:bg-secondary dark:text-foreground dark:hover:bg-secondary'
                    }`}
                  >
                    {option.label}
                  </li>
                );
              })}
            </React.Fragment>
          ))}
        </ul>
      )}
    </div>
  );
};
